import { useState } from 'react';
import { Download, Loader2, AlertCircle, FileText, CheckCircle } from 'lucide-react';
import api from '../lib/api';

interface ExportConfig {
  type: 'organizations' | 'fields' | 'questionnaires' | 'entries' | 'audit-logs' | 'time-series';
  fieldId?: string;
  format: 'csv' | 'xlsx' | 'json';
  dateFrom: string;
  dateTo: string;
}

const exportTypes = [
  { value: 'organizations', label: 'Organisationen' },
  { value: 'fields', label: 'Datenfelder' },
  { value: 'questionnaires', label: 'Fragebögen' },
  { value: 'entries', label: 'Dateneinträge' },
  { value: 'audit-logs', label: 'Audit Logs' },
  { value: 'time-series', label: 'Zeitreihe (Tagesendwerte)' },
] as const;

export default function ExportPage() {
  const [config, setConfig] = useState<ExportConfig>({
    type: 'entries',
    format: 'csv',
    dateFrom: '',
    dateTo: '',
    fieldId: '',
  });
  const [fields, setFields] = useState<any[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setResult(null);

    try {
      const params: Record<string, string> = { format: config.format };
      if (config.dateFrom) params.dateFrom = config.dateFrom;
      if (config.dateTo) params.dateTo = config.dateTo;
      if (config.fieldId) params.fieldId = config.fieldId;

      const endpoint = config.type === 'time-series' ? '/data-fields/history/export' : `/export/${config.type}`;
      const res = await api.get(endpoint, {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);

      const a = window.document.createElement('a');
      a.href = url;
      const ext = config.format === 'csv' ? 'csv' : config.format === 'json' ? 'json' : 'xlsx';
      a.download = `${config.type}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();

      window.URL.revokeObjectURL(url);
      setResult({ success: true, message: 'Export erfolgreich gestartet' });
    } catch {
      setResult({ success: false, message: 'Export fehlgeschlagen' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Download size={28} className="text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Export</h1>
          <p className="text-sm text-gray-500">Daten exportieren</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Export konfigurieren</h2>

          <div className="space-y-4">
            <div>
              <label className="label">Daten auswählen</label>
              <select
                className="input-field"
                value={config.type}
                onChange={(e) => setConfig({ ...config, type: e.target.value as ExportConfig['type'] })}
              >
                {exportTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Format</label>
              <div className="grid grid-cols-3 gap-3">
                {(['csv', 'xlsx', 'json'] as const).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setConfig({ ...config, format: fmt })}
                    className={`btn-secondary py-3 flex-col items-center gap-1 h-auto ${
                      config.format === fmt ? 'ring-2 ring-primary border-primary' : ''
                    }`}
                  >
                    <FileText size={20} className={
                      fmt === 'csv' ? 'text-accent-green-dark' :
                      fmt === 'xlsx' ? 'text-accent-blue' : 'text-accent-violet'
                    } />
                    <span className="text-xs font-medium uppercase">{fmt}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Von</label>
                <input
                  type="date"
                  className="input-field"
                  value={config.dateFrom}
                  onChange={(e) => setConfig({ ...config, dateFrom: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Bis</label>
                <input
                  type="date"
                  className="input-field"
                  value={config.dateTo}
                  onChange={(e) => setConfig({ ...config, dateTo: e.target.value })}
                />
              </div>
            </div>

            {config.type === 'time-series' && (
              <div>
                <label className="label">Datenfeld (optional)</label>
                <select className="input-field" value={config.fieldId || ""}
                  onChange={(e) => {
                    setConfig({ ...config, fieldId: e.target.value });
                    if (!fields.length) {
                      setLoadingFields(true);
                      import("../lib/api").then(mod => mod.default.get("/data-fields", { params: { limit: 200 } }).then(r => {
                        setFields(Array.isArray(r.data) ? r.data : (r.data?.data ?? []));
                        setLoadingFields(false);
                      }).catch(() => setLoadingFields(false)));
                    }
                  }}>
                  <option value="">Alle Felder</option>
                  {fields.map((f: any) => (
                    <option key={f.id} value={f.id}>{f.label || f.name}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              onClick={handleExport}
              disabled={isExporting}
              className="btn-primary w-full"
            >
              {isExporting ? (
                <><Loader2 size={18} className="animate-spin" /> Exportiere...</>
              ) : (
                <><Download size={18} /> Export starten</>
              )}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {result && (
            <div className={`card border-2 ${
              result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
            }`}>
              <div className="flex items-center gap-3">
                {result.success ? (
                  <CheckCircle size={24} className="text-green-600 shrink-0" />
                ) : (
                  <AlertCircle size={24} className="text-red-600 shrink-0" />
                )}
                <p className={`text-sm font-medium ${
                  result.success ? 'text-green-800' : 'text-red-800'
                }`}>{result.message}</p>
              </div>
            </div>
          )}

          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-2">Export-Optionen</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <Download size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>CSV: Komma-getrennt, UTF-8 mit BOM</span>
              </li>
              <li className="flex items-start gap-2">
                <Download size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>Excel: Formatierte Tabellen (.xlsx)</span>
              </li>
              <li className="flex items-start gap-2">
                <Download size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>JSON: Vollständige Daten-Struktur</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
